import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {
  getHouseholdLeaveResult,
  HouseholdLeaveResult,
  requestHouseholdLeave,
  WizardApiError,
} from '../../../api'

type Props = {
  sessionId: string
  householdMemberId: string
  mobileVerifiedAtUtc?: string
  name: string
  isSelf: boolean
  onHide: () => void
  onCompleted: () => void | Promise<void>
}

const HouseholdLeaveModal: React.FC<Props> = ({
  sessionId,
  householdMemberId,
  mobileVerifiedAtUtc,
  name,
  isSelf,
  onHide,
  onCompleted,
}) => {
  // Rejoining reuses the member ID but starts a new verified participation.
  const storageKey = `household-leave:${sessionId}:${householdMemberId}${
    mobileVerifiedAtUtc ? `:${mobileVerifiedAtUtc}` : ''
  }`
  const [eventId, setEventId] = useState<string | null>(() => sessionStorage.getItem(storageKey))
  const [result, setResult] = useState<HouseholdLeaveResult | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!eventId) return
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      try {
        const next = await getHouseholdLeaveResult(sessionId, eventId, controller.signal)
        if (controller.signal.aborted) return
        setResult(next)
        setError(null)
        if (next.processingState === 'completed') {
          sessionStorage.removeItem(storageKey)
          await onCompleted()
          return
        }
        if (next.processingState === 'manual_review') return
      } catch (failure) {
        if (controller.signal.aborted) return
        if (failure instanceof WizardApiError && failure.status === 404) {
          sessionStorage.removeItem(storageKey)
          setEventId(null)
          setResult(null)
          setError(
            'The previous household change is no longer available. Please confirm this change again.'
          )
          return
        }
        setError(
          failure instanceof Error
            ? failure.message
            : 'Unable to check the household change. Please try again.'
        )
      }
      timer = setTimeout(poll, 2000)
    }
    void poll()
    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [eventId, sessionId, storageKey, onCompleted])

  const confirm = async () => {
    if (submitting || eventId) return
    setSubmitting(true)
    setError(null)
    try {
      const leave = await requestHouseholdLeave(sessionId, householdMemberId)
      sessionStorage.setItem(storageKey, leave.leaveEventId)
      setEventId(leave.leaveEventId)
      setResult(leave)
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Unable to request the household change. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal show onHide={onHide} centered aria-labelledby='household-leave-title'>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title id='household-leave-title'>
          {isSelf ? 'Leave this household?' : `Remove ${name}?`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          {isSelf ? 'You will be removed' : `${name} will be removed`} from this household and its
          active applications. Updated applications will be sent to the property managers.
        </p>
        <p>
          {isSelf ? 'Your' : 'Their'} DOORA Card, personal information, income, documents, pets and
          vehicles will remain unchanged and available for future applications.
        </p>
        {eventId && (
          <p role='status'>
            {result?.processingState === 'manual_review'
              ? result.message || 'This household change needs support review.'
              : result?.message ||
                'Updating the household applications. You can close this window and check again later.'}
          </p>
        )}
        {error && (
          <p className='text-danger' role='alert'>
            {error}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onHide} disabled={submitting}>
          {eventId ? 'Close' : 'Cancel'}
        </button>
        {!eventId && (
          <button
            type='button'
            className='btn btn-danger'
            onClick={() => void confirm()}
            disabled={submitting}
          >
            {submitting ? 'Processing...' : isSelf ? 'Leave this household' : 'Remove member'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default HouseholdLeaveModal
